import { ReactNode } from 'react';

import {
  Router as ResourceRouter,
  StaticRouter,
  BrowserHistory,
  useRouter,
} from 'react-resource-router';
import { RouterProps as ResourceRouterProps } from 'react-resource-router/build/cjs/controllers/router/types';

import { usePostOfficeRouteUpdate } from '@atlassian/post-office-placement-remote';

import { Routes } from 'src/router/routes/types';
import { useUnlinkedWorkspaceFlag } from 'src/sections/global/components/flags/unlinked-workspace-flag';
import { getResourceStoreSsrState } from 'src/utils/ssr-hydration';

type RouterProps = Omit<ResourceRouterProps, 'history' | 'isStatic'> & {
  history: BrowserHistory;
  routes: Routes;
  children: ReactNode;
  isSsr: boolean;
  location: string;
  isStatic?: boolean;
};

const PostOfficeRouteListener = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [route] = useRouter();

  // This hook updates the Post Office route context with the current route.
  // This allows Post Office placements to re-render/update based on the current route (referred to as a "scope change").
  // e.g. ["/:workspaceSlug/workspace/pull-requests"]
  usePostOfficeRouteUpdate([route.match.path]);

  return <>{children}</>;
};

export const Router = ({
  history,
  routes,
  children,
  isSsr,
  location,
  isStatic = false,
  ...rest
}: RouterProps) => {
  useUnlinkedWorkspaceFlag();

  if (isSsr) {
    return (
      <StaticRouter routes={routes} location={location}>
        <PostOfficeRouteListener>{children}</PostOfficeRouteListener>
      </StaticRouter>
    );
  }

  return (
    <ResourceRouter
      history={history}
      routes={routes}
      isStatic={isStatic}
      resourceData={getResourceStoreSsrState()}
      {...rest}
    >
      <PostOfficeRouteListener>{children}</PostOfficeRouteListener>
    </ResourceRouter>
  );
};
